import type { SxProps, Theme } from '@mui/material';
import { COPILOT_CHAT_WAITING_CLASS, COPILOT_SEND_FOOTER_HEIGHT } from './copilotWebChat.constants';
import { copilotWebChatTypographySx } from './copilotWebChatTypographySx';

// --- Connecting state ---

export const copilotConnectingRootSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: 3,
  py: 6,
  background:
    'radial-gradient(ellipse at 50% 40%, rgba(134, 97, 197, 0.08) 0%, transparent 65%), linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 100%)',
};

export const copilotConnectingContentSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2.5,
  maxWidth: 280,
  textAlign: 'center',
};

export const copilotConnectingIconShellSx: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(0, 120, 212, 0.08) 0%, rgba(134, 97, 197, 0.12) 100%)',
  '@keyframes copilotPulse': {
    '0%, 100%': { transform: 'scale(1)', opacity: 1 },
    '50%': { transform: 'scale(1.06)', opacity: 0.85 },
  },
  animation: 'copilotPulse 2.4s ease-in-out infinite',
};

export const copilotConnectingTitleSx: SxProps<Theme> = {
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1F2937',
  letterSpacing: '-0.02em',
  mb: 0.75,
};

export const copilotConnectingSubtitleSx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  color: '#6B7280',
  lineHeight: 1.6,
};

export const copilotConnectingDotsRowSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.75,
  mt: 0.5,
};

export const getCopilotConnectingDotSx = (index: number): SxProps<Theme> => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #0078D4 0%, #8661C5 100%)',
  opacity: 0.4,
  '@keyframes dotBounce': {
    '0%, 80%, 100%': { transform: 'scale(0.75)', opacity: 0.35 },
    '40%': { transform: 'scale(1)', opacity: 1 },
  },
  animation: `dotBounce 1.4s ease-in-out ${index * 0.16}s infinite`,
});

// --- Chat shell ---

export const copilotConnectingShellSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

export const copilotErrorStateSx: SxProps<Theme> = {
  p: 2,
  '& .MuiAlert-root': {
    mb: 2,
  },
};

export const copilotSuggestionsOverlaySx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  bottom: COPILOT_SEND_FOOTER_HEIGHT,
  zIndex: 10,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  px: 2.5,
  py: 3,
  pointerEvents: 'auto',
  backgroundColor: '#FAFBFC',
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '2px',
  },
};

export const copilotThinkingIndicatorSx: SxProps<Theme> = {
  position: 'absolute',
  left: 14,
  bottom: COPILOT_SEND_FOOTER_HEIGHT + 10,
  zIndex: 5,
  pointerEvents: 'none',
};

const copilotChatWaitingSx: SxProps<Theme> = {
  [`&.${COPILOT_CHAT_WAITING_CLASS} .webchat__send-box`]: {
    pointerEvents: 'none',
    opacity: '1 !important',
    visibility: 'visible !important',
  },
  [`&.${COPILOT_CHAT_WAITING_CLASS} .webchat__send-box__main, &.${COPILOT_CHAT_WAITING_CLASS} .webchat__send-box__main:focus-within`]:
    {
      backgroundColor: '#F8FAFC !important',
      borderColor: '#E2E8F0 !important',
      boxShadow: 'none !important',
    },
  [`&.${COPILOT_CHAT_WAITING_CLASS} .webchat__send-box-text-box__input, &.${COPILOT_CHAT_WAITING_CLASS} .webchat__send-box-text-box__html-text`]:
    {
      cursor: 'not-allowed',
      color: '#64748B !important',
      WebkitTextFillColor: '#64748B !important',
    },
  [`&.${COPILOT_CHAT_WAITING_CLASS} .webchat__send-box__button`]: {
    cursor: 'not-allowed',
    backgroundColor: '#94A3B8 !important',
    boxShadow: 'none !important',
  },
};

export const getCopilotChatRootSx = (showSuggestions: boolean): SxProps<Theme> => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  '& .webchat': {
    flex: 1,
    minHeight: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  ...copilotWebChatTypographySx,
  ...(showSuggestions && {
    '& .webchat__basic-transcript': {
      visibility: 'hidden',
    },
  }),
  ...copilotChatWaitingSx,
});
